import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil, Search, X, ArrowUp, ArrowDown } from 'lucide-react';

import useAdminUsers from '../../hooks/useAdminUsers';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { parseApiError } from '../../utils/apiError';

export default function AdminUsers() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // null | 'name' | 'email' | 'status'
  const [sortDir, setSortDir] = useState('asc');

  const sort = sortField ? `${sortField}_${sortDir}` : 'newest';
  const { items, total, pages, loading, update } = useAdminUsers({ page, q, sort, limit: 20 });

  const onSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortField(null);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (field) =>
    sortField === field ? (
      sortDir === 'asc' ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />
    ) : null;

  const onSearch = (e) => {
    e.preventDefault();
    setQ(query.trim());
    setPage(1);
  };

  const onClearSearch = () => {
    setQuery('');
    setQ('');
    setPage(1);
  };

  const toggleActive = async (u) => {
    try {
      await update(u._id, { active: !u.active });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not update user', { variant: 'error' });
    }
  };

  const fullName = (u) => [u.fName, u.lName].filter(Boolean).join(' ') || '—';
  const roleLabel = (u) => u.REF_UserType?.Text || '—';
  const formattedDate = (u) => (u.dateCreated ? new Date(u.dateCreated).toLocaleDateString() : '—');

  return (
    <div>
      <div className="ss-admin__header">
        <h1>Users</h1>
      </div>

      <form className="ss-admin-search" onSubmit={onSearch} style={{ marginTop: 'var(--ss-space-4)' }}>
        <div className="ss-admin-search__field">
          <input
            className="ss-input"
            type="search"
            placeholder="Search users…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search users"
          />
          {query && (
            <button
              type="button"
              className="ss-admin-search__clear"
              onClick={onClearSearch}
              aria-label="Clear search"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        <Button type="submit" variant="ghost" size="sm"><Search size={16} aria-hidden="true" /> Search</Button>
      </form>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading users" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="ss-state"><h2>{q ? 'No users match your search' : 'No users yet'}</h2></div>
      ) : (
        <>
          <Card className="ss-admin-table-wrap" style={{ marginTop: 'var(--ss-space-5)', padding: 0 }}>
            <div className="ss-table-scroll">
            <table className="ss-admin-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('name')}>
                      Name {sortIcon('name')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('email')}>
                      Email {sortIcon('email')}
                    </button>
                  </th>
                  <th>Role</th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('status')}>
                      Status {sortIcon('status')}
                    </button>
                  </th>
                  <th className="ss-admin-table__muted">Created</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u._id}>
                    <td>
                      <Link to={`/admin/users/${u._id}`} className="ss-admin-table__title">{fullName(u)}</Link>
                    </td>
                    <td className="ss-admin-table__muted">{u.email}</td>
                    <td className="ss-admin-table__muted">{roleLabel(u)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="ss-admin-toggle"
                        onClick={() => toggleActive(u)}
                        title={u.active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        <Badge className={u.active ? 'ss-badge--active' : ''}>{u.active ? 'Active' : 'Inactive'}</Badge>
                      </button>
                    </td>
                    <td className="ss-admin-table__muted">{formattedDate(u)}</td>
                    <td>
                      <div className="ss-admin-actions">
                        <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/users/${u._id}`)} aria-label={`Edit ${fullName(u)}`}>
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          <div className="ss-admin-cards" style={{ marginTop: 'var(--ss-space-5)' }}>
            {items.map((u) => (
              <div className="ss-admin-card" key={u._id}>
                <div className="ss-admin-card__row">
                  <Link to={`/admin/users/${u._id}`} className="ss-admin-card__title">{fullName(u)}</Link>
                  <button
                    type="button"
                    className="ss-admin-toggle"
                    onClick={() => toggleActive(u)}
                    title={u.active ? 'Click to deactivate' : 'Click to activate'}
                  >
                    <Badge className={u.active ? 'ss-badge--active' : ''}>{u.active ? 'Active' : 'Inactive'}</Badge>
                  </button>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Email</span>
                  <span className="ss-admin-table__muted">{u.email}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Role</span>
                  <span className="ss-admin-table__muted">{roleLabel(u)}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Created</span>
                  <span className="ss-admin-table__muted">{formattedDate(u)}</span>
                </div>
                <div className="ss-admin-card__row">
                  <div className="ss-admin-actions">
                    <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/users/${u._id}`)} aria-label={`Edit ${fullName(u)}`}>
                      <Pencil size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pages > 1 && (
        <div className="ss-pager" style={{ marginTop: 'var(--ss-space-4)' }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
          <span className="ss-pager__info">Page {page} of {pages} · {total} total</span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
